// src/pages/Downloads.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../context/AuthContext";
import AdSlot from "../components/AdSlot";

const Skeletons = () => (
  <div className="downloads-grid" aria-busy="true" aria-label="Loading downloads">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="download-card download-card--skeleton" />
    ))}
  </div>
);

const Downloads = () => {
  const { user }                  = useAuth();
  const [downloads, setDownloads] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (cancelled) return;
        const data      = snap.exists() ? snap.data() : {};
        const sorted    = [...(data.downloads || [])].reverse();
        setDownloads(sorted);
      } catch (err) {
        if (!cancelled) setError("Failed to load your downloads. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user]);

  // Not logged in
  if (!user) {
    return (
      <div className="page page--center">
        <div className="state-box">
          <span className="state-box__icon">🔒</span>
          <h2 className="state-box__title">Sign in to see your downloads</h2>
          <p className="state-box__sub" style={{ marginBottom: 24 }}>
            Keep track of every photo you've downloaded.
          </p>
          <Link to="/auth" className="btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {/* ── AD SLOT: banner ── */}
        <div className="ad-wrap--page">
          <AdSlot type="banner" />
        </div>

        {/* Header */}
        <div className="page-header">
          <h1 className="page-header__title">My Downloads</h1>
          {!loading && !error && (
            <p className="page-header__sub">
              {downloads.length === 0
                ? "No downloads yet"
                : `${downloads.length} photo${downloads.length !== 1 ? "s" : ""} downloaded`}
            </p>
          )}
        </div>

        {/* States */}
        {error && (
          <div className="state-box">
            <span className="state-box__icon">⚠️</span>
            <h2 className="state-box__title">Something went wrong</h2>
            <p className="state-box__sub">{error}</p>
          </div>
        )}

        {loading && <Skeletons />}

        {!loading && !error && downloads.length === 0 && (
          <div className="state-box">
            <span className="state-box__icon">📥</span>
            <h2 className="state-box__title">Nothing here yet</h2>
            <p className="state-box__sub" style={{ marginBottom: 24 }}>
              Explore our collection and download your first photo.
            </p>
            <Link to="/" className="btn-primary">Browse Photos</Link>
          </div>
        )}

        {!loading && !error && downloads.length > 0 && (
          <div className="downloads-grid">
            {downloads.map((item, i) => (
              <div key={`${item.photoId}-${i}`} className="download-card">
                <div className="download-card__thumb">
                  <img
                    src={item.thumbnail}
                    alt={`Photo by ${item.photographer}`}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="download-card__info">
                  <p className="download-card__photographer">{item.photographer}</p>
                  <div className="download-card__meta">
                    <span className="badge">{item.size}</span>
                    <span className="download-card__date">
                      {new Date(item.downloadedAt).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── AD SLOT: footer ── */}
        <AdSlot type="footer" />
      </div>
    </div>
  );
};

export default Downloads;
